/**
 * ReportsHubPage — top-level "Reports & Downloads" landing.
 *
 * Feb 2026 · Sidebar-consolidation pass.
 *
 * Presents every project the current user can access as a card with a
 * "Open Reports Panel" button. Clicking a project loads its per-project
 * Reports Panel (existing DataBank component at
 * /app/projects/{pid}/reports). No new backend calls — reuses the
 * existing `GET /api/projects` endpoint.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Archive, ArrowRight, Buildings, CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function ReportsHubPage() {
  const projectsQ = useQuery({
    queryKey: ["projects-list"],
    queryFn: () => api.get(`/projects`).then((r) => r.data),
    staleTime: 30_000,
  });

  const list = Array.isArray(projectsQ.data)
    ? projectsQ.data
    : (projectsQ.data?.projects || []);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50" data-testid="reports-hub">
      <header className="shrink-0 bg-white border-b border-slate-200">
        <div className="px-6 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-brand-blue/10 border border-brand-blue/25 flex items-center justify-center">
            <Archive size={18} className="text-brand-blue" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">Reports & Downloads · Single hub</div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none">
              Reports & Downloads
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Every generated report — CPR, technical, GIS map packs, drillhole log books, block models — lives inside the per-project Reports Panel below.
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4"
                data-testid="reports-hub-main">
        <div className="max-w-6xl mx-auto pb-8">
          {projectsQ.isLoading && (
            <div className="text-center text-[12px] text-slate-500 py-8">
              <CircleNotch size={16} className="animate-spin inline mr-1" /> Loading projects…
            </div>
          )}
          {projectsQ.isError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-800">
              Could not load projects: {projectsQ.error?.message}
            </div>
          )}
          {!projectsQ.isLoading && list.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <Buildings size={22} className="text-slate-400 mx-auto mb-2" weight="duotone" />
              <div className="text-[13px] text-slate-700 font-semibold">No projects yet</div>
              <div className="text-[11.5px] text-slate-500 mt-1">
                Create a project first to generate reports.
              </div>
              <Link to="/app/projects/new"
                       className="inline-flex items-center gap-1 mt-3 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-3 h-9 text-[11.5px]"
                       data-testid="btn-goto-new-project">
                Create project <ArrowRight size={11} />
              </Link>
            </div>
          )}
          {list.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.map((p) => (
                <Link key={p.id}
                         to={`/app/projects/${p.id}/reports`}
                         className="group rounded-lg border-2 border-slate-200 bg-white p-4 hover:border-brand-blue/60 hover:shadow-md transition-all flex flex-col"
                         data-testid={`reports-hub-project-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Buildings size={14} className="text-brand-blue" weight="duotone" />
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 truncate">
                      {p.commodity || "Project"}
                    </div>
                  </div>
                  <div className="font-heading font-bold text-[14px] text-slate-900 truncate group-hover:text-brand-blue">
                    {p.name || p.id}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {p.country || "—"} · {p.status || "Active"}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1 text-[11.5px] text-brand-blue font-semibold">
                    <Archive size={11} weight="fill" /> Open Reports Panel <ArrowRight size={11} className="ml-auto group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
